import { get } from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Text, FormElements, Spinner } from '@custom-components';
import { TableHead, TableBody, TableRow } from '@mui/material';
import TableSortLabel from './TableSortLabel';
import { TableWrapper, StyledCell, StyledTable, StyledTablePagination } from './SelectTable.styles';
import { ReactChildrenPropType } from '../../../lib/commonPropTypes';

class SortingTable extends Component {
  static tableSort(array, cmp) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = cmp(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  /** Function to tell the label/Head if you are sorting in ascending or descending order */
  static desc(a, b, orderBy) {
    if (b[orderBy].value !== undefined && a[orderBy].value !== undefined) {
      if (b[orderBy].value < a[orderBy].value) {
        return -1;
      }
      if (b[orderBy].value > a[orderBy].value) {
        return 1;
      }
      return 0;
    }
    if (b[orderBy].value !== undefined) {
      return -1;
    }
    if (a[orderBy].value !== undefined) {
      return 1;
    }
    return 0;
  }

  /**  */
  static getSorting(order, orderBy) {
    return order === 'desc' ? (a, b) => SortingTable.desc(a, b, orderBy) : (a, b) => -SortingTable.desc(a, b, orderBy);
  }

  constructor(props) {
    super(props);

    const { orderBy, order } = this.props;

    this.state = {
      page: 0,
      rowsPerPage: 10,
      order,
      orderBy
    };

    this.handleRequestSort = this.handleRequestSort.bind(this);
    this.handleChangePage = this.handleChangePage.bind(this);
    this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this);
  }

  /** change current visible page */
  handleChangePage = (event, page) => {
    const { onPageChange } = this.props;

    onPageChange(event, page);
    this.setState({ page });
  };

  /** Change how many rows show on each page */
  handleChangeRowsPerPage = (event) => {
    const { onUpdateRowsPerPage } = this.props;

    onUpdateRowsPerPage(event);
    this.setState({ rowsPerPage: event.target.value, page: 0 });
  };

  /** Update sort orderBy and order onClick  */
  handleRequestSort(event, property) {
    const { orderBy, order } = this.state;
    const { onSort } = this.props;

    let newOrder = order;

    if (orderBy === property && order === 'desc') {
      newOrder = 'asc';
    } else if (orderBy === property && order === 'asc') {
      newOrder = 'desc';
    }

    onSort(property, newOrder);

    this.setState({ order: newOrder, orderBy: property });
  }

  isSelected = (id) => {
    const { selected } = this.props;
    return selected.indexOf(id) !== -1;
  };

  render() {
    const {
      minTableWidth,
      rows,
      children,
      headerCellsConfig,
      handleSelectAllClick,
      selected,
      contentCellsConfig,
      handleSelectClick,
      loading
    } = this.props;
    const { rowsPerPage, page, order, orderBy } = this.state;

    const emptyRows = rowsPerPage - Math.min(rowsPerPage, children.length - page * rowsPerPage);

    const tableLayout = SortingTable.tableSort(children, SortingTable.getSorting(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    return (
      <>
        <TableWrapper>
          <StyledTable minTableWidth={minTableWidth}>
            {/* head */}
            <TableHead>
              <TableRow>
                <StyledCell padding="checkbox" style={{ width: '40px' }}>
                  <FormElements.Checkbox
                    indeterminate={selected.length > 0 && selected.length < children.length}
                    checked={selected.length === children.length && selected.length !== 0}
                    onChange={handleSelectAllClick}
                  />
                </StyledCell>
                {rows?.map((row) => {
                  const { id, label } = row;
                  const cellConfigProps = get(headerCellsConfig, `${id}`, {});
                  return (
                    <StyledCell key={row.id} sortDirection={orderBy === row.id ? order : false} {...cellConfigProps}>
                      {id !== 'actions' && (
                        <TableSortLabel
                          active={orderBy === row.id}
                          direction={order}
                          onClick={(e) => {
                            this.handleRequestSort(e, row.id);
                          }}
                        >
                          <Text.CopyMini noMargins>{label}</Text.CopyMini>
                        </TableSortLabel>
                      )}
                    </StyledCell>
                  );
                })}
              </TableRow>
            </TableHead>
            {/* body */}
            {children && children.length !== 0 && !loading && (
              <TableBody>
                {tableLayout.map((child) => {
                  const isSelected = this.isSelected(child.id);
                  return (
                    <TableRow key={child.id} selected={isSelected}>
                      <StyledCell padding="checkbox">
                        <FormElements.Checkbox checked={isSelected} onChange={(e) => handleSelectClick(e, child.id)} />
                      </StyledCell>
                      {Object.entries(child).map(([key, item]) => {
                        if (key === 'id') return null;
                        const cellConfigProps = get(contentCellsConfig, `${key}`, {});
                        return (
                          <StyledCell key={key} {...cellConfigProps}>
                            {item.label}
                          </StyledCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 49 * emptyRows }}>
                    <StyledCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            )}
          </StyledTable>
        </TableWrapper>
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'space-around' }}>
            <Spinner thickness={4} size={50} />
          </div>
        )}
        {/* pagination */}
        {children.length > 5 && (
          <StyledTablePagination
            component="div"
            count={children.length}
            rowsPerPage={rowsPerPage}
            page={page}
            backIconButtonProps={{
              'aria-label': 'Previous Page'
            }}
            nextIconButtonProps={{
              'aria-label': 'Next Page'
            }}
            onPageChange={this.handleChangePage}
            onRowsPerPageChange={this.handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 20]}
          />
        )}
      </>
    );
  }
}

SortingTable.propTypes = {
  /** Array of content objects to display in table */
  children: ReactChildrenPropType,
  /** Object with keys matching row object IDs containing @MUITableCell properties and valid td attributes */
  contentCellsConfig: PropTypes.shape({}),
  /** Object with keys matching row object IDs containing @MUITableCell properties and valid td attributes */
  headerCellsConfig: PropTypes.shape({}),
  /** Min width of the table passed via string (900px default) */
  minTableWidth: PropTypes.string,
  /** Array of label objects to display at the top of the table */
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      width: PropTypes.string
    })
  ).isRequired,
  /** Order by asc or desc */
  order: PropTypes.oneOf(['asc', 'desc']),
  /** Column the table is being sorted by */
  orderBy: PropTypes.string.isRequired,
  onPageChange: PropTypes.func,
  onUpdateRowsPerPage: PropTypes.func,
  onSort: PropTypes.func,
  handleSelectAllClick: PropTypes.func,
  handleSelectClick: PropTypes.func,
  selected: PropTypes.arrayOf(PropTypes.number),
  loading: PropTypes.bool
};

SortingTable.defaultProps = {
  children: undefined,
  contentCellsConfig: {},
  headerCellsConfig: {},
  minTableWidth: '900px',
  handleSelectAllClick: () => {},
  handleSelectClick: () => {},
  onPageChange: () => {},
  onUpdateRowsPerPage: () => {},
  onSort: () => {},
  selected: [],
  order: 'asc',
  loading: false
};

export default SortingTable;
